import { useState, useMemo } from "react";
import { useAppData } from "../../lib/AppDataContext";
import { mediaApi } from "../../lib/mediaApi";
import { Card } from "../../components/shared/Card";
import { Modal } from "../../components/shared/Modal";
import { toast } from "../../components/shared/Toast";

function slugify(t) { return String(t || "").toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-"); }

export function LocalitiesView() {
  const { cities, localities, mastersLoading, addLocality, updateLocality, toggleLocalityActive, deleteLocality } = useAppData();
  const [cityFilter, setCityFilter] = useState("all");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const filtered = useMemo(() => {
    if (cityFilter === "all") return localities;
    return localities.filter((l) => l.cityId === cityFilter);
  }, [localities, cityFilter]);

  if (mastersLoading) {
    return <div className="p-6"><Card><div className="p-10 text-center text-slate-500 text-sm">Loading…</div></Card></div>;
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Localities</h1>
          <p className="text-xs text-slate-500 mt-0.5">{filtered.length} of {localities.length} shown</p>
        </div>
        <button onClick={() => setAdding(true)} className="shrink-0 whitespace-nowrap px-4 py-2 bg-navy text-white rounded text-sm font-medium hover:bg-navy/90">+ Add locality</button>
      </div>

      <Card className="overflow-hidden">
        <div className="p-3 border-b border-slate-200 flex gap-2 items-center">
          <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded text-sm">
            <option value="all">All cities</option>
            {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <div className="overflow-auto max-h-[70vh]"><table className="w-full text-sm min-w-[640px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="sticky top-0 z-20 text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-4 py-2 font-semibold">Name</th>
              <th className="px-4 py-2 font-semibold">Slug</th>
              <th className="px-4 py-2 font-semibold">City</th>
              <th className="px-4 py-2 font-semibold">Area</th>
              <th className="px-4 py-2 font-semibold text-center">Properties</th>
              <th className="px-4 py-2 font-semibold">Status</th>
              <th className="px-4 py-2 font-semibold text-right"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No localities match.</td></tr>}
            {filtered.map((l) => (
              <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{l.name}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">/{l.slug}</td>
                <td className="px-4 py-3 text-slate-700">{l.city?.name || "—"}</td>
                <td className="px-4 py-3 text-slate-700 text-xs">{l.area || "—"}</td>
                <td className="px-4 py-3 text-center text-slate-700">{l._count?.properties ?? 0}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block px-2 py-0.5 rounded border text-xs ${l.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
                    {l.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right space-x-1 whitespace-nowrap">
                  <button onClick={() => toggleLocalityActive(l.id).catch((e) => toast(e?.message || "Failed"))} className="text-xs text-slate-600 hover:underline">{l.active ? "Deactivate" : "Activate"}</button>
                  <span className="text-slate-300">·</span>
                  <button onClick={() => setEditing(l)} className="text-xs text-navy hover:underline">Edit</button>
                  <span className="text-slate-300">·</span>
                  <button onClick={() => setDeleting(l)} className="text-xs text-red-600 hover:underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>

      {(adding || editing) && (
        <LocalityFormModal
          key={editing ? editing.id : "new"}
          initial={editing}
          cities={cities}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSave={async (data) => {
            try {
              if (editing) { await updateLocality(editing.id, data); toast("Locality updated"); }
              else { await addLocality(data); toast("Locality created"); }
              setAdding(false); setEditing(null);
            } catch (err) { toast(err?.message || "Failed"); }
          }}
        />
      )}
      {deleting && (
        <Modal onClose={() => setDeleting(null)} title="Delete locality?" size="sm">
          <div className="p-4 space-y-3 text-sm">
            <p>Soft-delete <strong>{deleting.name}</strong>?</p>
            <p className="text-xs text-amber-700">Only allowed if no active properties depend on this locality.</p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setDeleting(null)} className="px-3 py-1.5 text-xs border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
              <button onClick={async () => {
                try { await deleteLocality(deleting.id); toast("Locality deleted"); setDeleting(null); }
                catch (err) { toast(err?.message || "Failed"); }
              }} className="px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

function LocalityFormModal({ initial, cities, onClose, onSave }) {
  const [form, setForm] = useState(initial || {
    name: "", slug: "", cityId: cities[0]?.id || "", area: "", description: "", heroImageUrl: "", active: true, sortOrder: 0,
  });
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);

  async function handleHeroImage(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast("Please upload an image file"); return; }
    setUploadingImg(true);
    try {
      const res = await mediaApi.uploadImage(file);
      if (!res?.url) throw new Error("Upload returned no URL");
      setForm((f) => ({ ...f, heroImageUrl: res.url }));
    } catch (err) {
      toast(err?.message || "Image upload failed");
    } finally {
      setUploadingImg(false);
    }
  }

  return (
    <Modal onClose={onClose} title={initial ? "Edit locality" : "Add locality"} size="md" dismissOnBackdrop={false}>
      <form onSubmit={async (e) => {
        e.preventDefault();
        if (!form.name || !form.cityId) return;
        setSaving(true);
        // Slug auto-generated from name (silent); keep existing slug on edit.
        const payload = { ...form, slug: form.slug || slugify(form.name) };
        try { await onSave(payload); } finally { setSaving(false); }
      }} className="p-4 space-y-3 text-sm min-h-0 overflow-y-auto">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Name *</label>
          <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" required />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">City *</label>
          <select value={form.cityId} onChange={(e) => setForm((f) => ({ ...f, cityId: e.target.value }))} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" required>
            <option value="">— select —</option>
            {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Area</label>
          <input value={form.area || ""} onChange={(e) => setForm((f) => ({ ...f, area: e.target.value }))} placeholder="West Pune" className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
          <textarea rows={3} value={form.description || ""} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm" placeholder="Short paragraph about the locality" />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Hero image</label>
          <div className="flex items-center gap-3">
            <div className="w-28 h-20 shrink-0 rounded-lg ring-1 ring-inset ring-slate-200 bg-white grid place-items-center overflow-hidden">
              {form.heroImageUrl
                ? <img src={form.heroImageUrl} alt="Hero" className="w-full h-full object-cover" />
                : <span className="text-[10px] text-slate-400">No image</span>}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="inline-flex items-center gap-2 text-sm text-slate-600 rounded-lg ring-1 ring-dashed ring-slate-300 px-3 py-2 hover:bg-slate-50 cursor-pointer w-fit">
                {uploadingImg ? "Uploading…" : (form.heroImageUrl ? "Replace image" : "Upload image")}
                <input type="file" accept="image/*" className="hidden" disabled={uploadingImg} onChange={(e) => handleHeroImage(e.target.files?.[0])} />
              </label>
              {form.heroImageUrl && (
                <button type="button" onClick={() => setForm((f) => ({ ...f, heroImageUrl: "" }))} className="text-xs text-red-600 hover:underline w-fit">Remove image</button>
              )}
              <p className="text-[11px] text-slate-400">Shown on the locality cards + detail page.</p>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-1.5 text-xs bg-navy text-white rounded hover:bg-navy/90 disabled:opacity-50">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </Modal>
  );
}
